import { ChatCircleDots, FileMagnifyingGlass, MapPinArea, Path, Info } from "@phosphor-icons/react";
import { Reveal, SectionHeading } from "../Reveal";

const CARDS = [
    {
        icon: ChatCircleDots,
        title: "AI-Assisted Patient Intake",
        text: "Understand patient needs through intelligent, multilingual conversations and structured intake.",
        span: "lg:col-span-7",
    },
    {
        icon: FileMagnifyingGlass,
        title: "Medical Document Intelligence",
        text: "Organize and summarize patient-provided medical information to help create a structured case profile.",
        span: "lg:col-span-5",
        disclaimer: true,
    },
    {
        icon: MapPinArea,
        title: "Intelligent Provider Discovery",
        text: "Help patients identify relevant hospitals and specialists based on their healthcare needs, location, preferences and available options.",
        span: "lg:col-span-5",
    },
    {
        icon: Path,
        title: "Journey Intelligence",
        text: "Help coordinate appointments, documentation, travel, treatment milestones and follow-up.",
        span: "lg:col-span-7",
    },
];

export default function AISection() {
    return (
        <section data-testid="ai-section" className="py-28 lg:py-36 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <SectionHeading
                    chapter="03"
                    eyebrow="The Intelligence"
                    title="Intelligence behind the journey."
                    description="Combining artificial intelligence with healthcare expertise and human coordination to simplify complex cross-border healthcare journeys."
                    testid="ai-heading"
                />
                <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {CARDS.map((c, i) => (
                        <Reveal key={c.title} delay={0.08 * i} className={c.span}>
                            <div
                                data-testid={`ai-card-${i + 1}`}
                                className="group relative h-full overflow-hidden rounded-3xl border border-slate-100 bg-gradient-to-br from-white to-tint-slate p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_24px_48px_rgb(10,37,64,0.10)] transition-[transform,box-shadow] duration-300"
                            >
                                <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-tint-green opacity-60 group-hover:scale-125 transition-transform duration-500" aria-hidden="true" />
                                <div className="relative">
                                    <div className="w-14 h-14 rounded-2xl bg-brand-blue flex items-center justify-center text-white shadow-lg shadow-brand-blue/20">
                                        <c.icon size={28} weight="duotone" />
                                    </div>
                                    <h3 className="mt-7 font-heading text-xl sm:text-2xl font-semibold text-brand-blue">{c.title}</h3>
                                    <p className="mt-3 text-base leading-relaxed text-slate-600 max-w-lg">{c.text}</p>
                                    {c.disclaimer && (
                                        <p data-testid="ai-disclaimer" className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-slate-500 italic">
                                            <Info size={14} className="mt-0.5 shrink-0 text-brand-green" />
                                            AI-generated information is intended to assist navigation and does not replace professional medical judgment.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
